import React, { memo, useState } from 'react';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { styled, SxProps, Theme } from '@mui/material/styles';

import { TOKENS } from '../../styles/tokens';

/**
 * PromoCodeField — the subtle "Code" (promo) entry control shared by the Jobs
 * post-a-job flows. Purely presentational: all pricing/validation lives in the
 * parent RTK Query; this component only renders three states off its props and
 * reports the code to apply/clear via `onApply`. States match design canvases
 * 3a / 3c / 3d:
 *   • empty / typing (3c) — mono/upper input + "Apply"
 *   • applied        (3a) — green dashed pill  "CODE ✓ $X off applied ✕"
 *   • invalid        (3d) — red input + inline red error line
 *
 * The word "coupon" is never shown to users: backend messages are scrubbed
 * ("coupon" → "code") and generic invalids fall back to friendly copy. Backend /
 * API names (couponApplied, couponCode, couponErrorMessage) are kept on the props
 * to match the existing service contract.
 */
export interface PromoCodeFieldProps {
  /** Seed the input (URL `?code=` / already-applied code). Read once on mount. */
  initialCode?: string;
  /** Response flag → render the applied pill (3a). */
  couponApplied?: boolean;
  /** Applied code shown in the pill. */
  couponCode?: string;
  /** "✓ $X off applied" — selected listing type only; omit → "✓ Code applied". */
  discountAmount?: number;
  /** Raw response message → render the invalid state (3d), scrubbed of "coupon". */
  couponErrorMessage?: string;
  /** Neutral info note — e.g. the code is valid but not for the selected listing type. */
  noteMessage?: string;
  /** Apply shows a spinner + is disabled while re-pricing. */
  isFetching?: boolean;
  /** Trimmed code to apply, or `undefined` to clear. */
  onApply: (code: string | undefined) => void;
}

// System monospace stack — no JetBrains Mono / web-font pull-in (per stack rules).
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const INVALID = '#d92d20';
const INVALID_RING = '#fbeae9';
const INPUT_ID = 'promo-code-input';
const ERROR_ID = 'promo-code-error';

const LABEL_SX: SxProps<Theme> = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: TOKENS.placeholder,
  mb: '6px',
};

/** Turn a backend coupon error into user copy that never says "coupon". */
export const normalizeCouponError = (raw: string | undefined, attemptedCode: string): string => {
  const msg = (raw || '').trim();
  // Generic invalid / not-found → friendly mockup copy.
  if (!msg || /invalid coupon code/i.test(msg)) {
    return `${attemptedCode} isn't a valid code. Check the spelling or try another.`;
  }
  // Specific reason (expired / usage limit / scope) → surface it, scrubbed of "coupon".
  const scrubbed = msg.replace(/coupon code/gi, 'code').replace(/coupon/gi, 'code');
  return scrubbed.charAt(0).toUpperCase() + scrubbed.slice(1);
};

const formatDollars = (n: number): string => (Number.isInteger(n) ? `${n}` : n.toFixed(2));

/** Native input styled for the "Code" box — sidesteps the app's global MUI
 *  OutlinedInput/InputBase overrides so the mockup treatment is exact. */
const CodeInput = styled('input', {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'hasClear',
})<{ hasError?: boolean; hasClear?: boolean }>(({ hasError, hasClear }) => ({
  width: 190,
  maxWidth: '100%',
  height: 40,
  boxSizing: 'border-box',
  border: `1.5px solid ${hasError ? INVALID : TOKENS.placeholder}`,
  borderRadius: 6,
  padding: hasClear ? '10px 34px 10px 14px' : '10px 14px',
  fontFamily: MONO,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0,
  textTransform: 'uppercase',
  color: TOKENS.ink,
  background: '#fff',
  outline: 'none',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  '&::placeholder': {
    color: TOKENS.placeholder,
    textTransform: 'none',
    fontWeight: 400,
    letterSpacing: 0,
  },
  '&:focus': {
    borderColor: hasError ? INVALID : TOKENS.placeholder,
    boxShadow: `0 0 0 3px ${hasError ? INVALID_RING : TOKENS.accentSoft}`,
  },
}));

const FieldLabel = styled('label')({
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: TOKENS.placeholder,
  marginBottom: 6,
});

export const PromoCodeField = memo(({
  initialCode,
  couponApplied,
  couponCode,
  discountAmount,
  couponErrorMessage,
  noteMessage,
  isFetching,
  onApply,
}: PromoCodeFieldProps) => {
  const [couponInput, setCouponInput] = useState(initialCode ?? '');
  const [lastSubmitted, setLastSubmitted] = useState((initialCode ?? '').trim());

  const trimmedInput = couponInput.trim();
  // Only show the invalid treatment while the field still holds the attempted code;
  // once the user edits, drop back to the plain typing state.
  const showError = Boolean(couponErrorMessage) && lastSubmitted !== '' && trimmedInput === lastSubmitted;
  const showClear = showError && trimmedInput !== '';
  const applyDisabled = !trimmedInput || trimmedInput === lastSubmitted || Boolean(isFetching);
  // Don't spin an empty Apply on the page's baseline load — only when a code is in play.
  const showSpinner = Boolean(isFetching) && (lastSubmitted !== '' || trimmedInput !== '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setCouponInput(val);
    // Editing away from a submitted/seeded code un-applies it in the parent (whether the
    // field is cleared OR changed to something new) so a stale — especially invalid — code
    // is never carried into checkout without a fresh Apply (P2). No-op while typing a
    // brand-new code (lastSubmitted still '').
    if (lastSubmitted !== '' && val.trim() !== lastSubmitted) {
      setLastSubmitted('');
      onApply(undefined);
    }
  };

  const handleApply = () => {
    const trimmed = couponInput.trim();
    if (!trimmed) return;
    setLastSubmitted(trimmed);
    onApply(trimmed);
  };

  const handleRemove = () => {
    setCouponInput('');
    setLastSubmitted('');
    onApply(undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!applyDisabled) handleApply();
    }
  };

  // ── Applied (3a) ────────────────────────────────────────────────────────────
  if (couponApplied) {
    const appliedCode = (couponCode || couponInput || '').trim();
    const savings = typeof discountAmount === 'number' && discountAmount > 0
      ? `✓ $${formatDollars(discountAmount)} off applied`
      : '✓ Code applied';

    return (
      <Box data-cy="promoCodeField">
        <Box component="span" sx={LABEL_SX}>Code</Box>
        <Box
          data-cy="promoCodePill"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            maxWidth: '100%',
            border: `1.5px dashed ${TOKENS.go}`,
            backgroundColor: TOKENS.goSoft,
            borderRadius: '6px',
            padding: '10px 14px',
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: MONO,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: 'uppercase',
              color: TOKENS.ink,
            }}
          >
            {appliedCode}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: TOKENS.goDeep,
              whiteSpace: 'nowrap',
            }}
          >
            {savings}
          </Box>
          <IconButton
            type="button"
            aria-label="Remove"
            data-cy="promoCodeRemove"
            onClick={handleRemove}
            disableRipple
            sx={{
              width: 17,
              height: 17,
              minWidth: 17,
              minHeight: 17,
              p: 0,
              borderRadius: '50%',
              backgroundColor: '#fff',
              color: TOKENS.goDeep,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: '#fff' },
            }}
          >
            <Box component="span" aria-hidden="true" sx={{ fontSize: 11, lineHeight: 1, fontWeight: 700 }}>✕</Box>
          </IconButton>
        </Box>
      </Box>
    );
  }

  // ── Empty / typing (3c) + Invalid (3d) ──────────────────────────────────────
  const attemptedCode = (lastSubmitted || trimmedInput).toUpperCase();

  return (
    <Box data-cy="promoCodeField">
      <FieldLabel htmlFor={INPUT_ID}>Code</FieldLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CodeInput
            id={INPUT_ID}
            data-cy="promoCodeInput"
            hasError={showError}
            hasClear={showClear}
            value={couponInput}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            aria-invalid={showError}
            {...(showError ? { 'aria-describedby': ERROR_ID } : {})}
          />
          {showClear && (
            <IconButton
              type="button"
              aria-label="Clear code"
              data-cy="promoCodeClear"
              onClick={handleRemove}
              disableRipple
              sx={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 20,
                height: 20,
                p: 0,
                color: INVALID,
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              <Box component="span" aria-hidden="true" sx={{ fontSize: 12, lineHeight: 1, fontWeight: 700 }}>✕</Box>
            </IconButton>
          )}
        </Box>
        <Button
          type="button"
          variant="outlined"
          data-cy="promoCodeApply"
          onClick={handleApply}
          disabled={applyDisabled}
          sx={{
            border: `2px solid ${TOKENS.ink}`,
            borderRadius: '6px',
            padding: '9px 18px',
            minWidth: 84,
            height: 40,
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
            textTransform: 'none',
            color: TOKENS.ink,
            backgroundColor: '#fff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: TOKENS.accentSoft,
              borderColor: TOKENS.ink,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              color: TOKENS.ink,
              borderColor: TOKENS.ink,
              opacity: 0.45,
            },
          }}
        >
          {showSpinner ? <CircularProgress size={16} thickness={5} sx={{ color: TOKENS.ink }} /> : 'Apply'}
        </Button>
      </Box>
      {showError && (
        <Box
          id={ERROR_ID}
          data-cy="promoCodeError"
          role="alert"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            mt: '8px',
            color: INVALID,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.35,
          }}
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              flex: '0 0 auto',
              width: 15,
              height: 15,
              borderRadius: '50%',
              backgroundColor: INVALID,
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >✕</Box>
          <span>
            {(!couponErrorMessage || /invalid coupon code/i.test(couponErrorMessage))
              ? (<><b>{attemptedCode}</b>{" isn't a valid code. Check the spelling or try another."}</>)
              : normalizeCouponError(couponErrorMessage, attemptedCode)}
          </span>
        </Box>
      )}
      {noteMessage && !showError && (
        <Box
          data-cy="promoCodeNote"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            mt: '8px',
            color: TOKENS.accent,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.35,
          }}
        >
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              flex: '0 0 auto',
              width: 15,
              height: 15,
              borderRadius: '50%',
              border: `1.5px solid ${TOKENS.accent}`,
              color: TOKENS.accent,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >i</Box>
          <span>{noteMessage}</span>
        </Box>
      )}
    </Box>
  );
});

PromoCodeField.displayName = 'PromoCodeField';

export default PromoCodeField;
